// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xGoogleAnalyticsState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { parseProductCategories } from "@guestsupply/XDL/Utilities";
import { ApiHandler, createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";

type HandlerType = ApiHandler<{
    includeCheckoutEvent: boolean | undefined;
    checkoutEventType: string;
}>;

const XngGaCheckoutStepsEvent: HandlerType = props => {
    const state = props.getState() as xGoogleAnalyticsState;
    const currentBillTo = getCurrentBillToState(state).value;
    const cart = getCurrentCartState(props.getState()).value;
    const session = getSession(state);
    const locationId = session?.pickUpWarehouse?.id;
    let discountTotal = 0;
    const promotionsDataView = getCurrentPromotionsDataView(state);
    if (promotionsDataView.value) {
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    const { includeCheckoutEvent, checkoutEventType } = props.parameter;

    if (includeCheckoutEvent) {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.

        const googleAnalyticsData = {
            event: checkoutEventType,
            ecommerce: {
                ...(checkoutEventType === GOOGLE_EVENTS.ADD_SHIPPING_INFO && {
                    shipping_tier: cart?.carrier?.description || "",
                }),
                ...(checkoutEventType === GOOGLE_EVENTS.ADD_PAYMENT_INFO && {
                    payment_type: cart?.paymentMethod?.description || "",
                }),
                currency: state.context.session.currency?.currencyCode,
                value: cart?.orderSubTotal,
                items: cart?.cartLines?.map((cartLine, idx) => ({
                    item_id: cartLine.erpNumber,
                    item_name: cartLine.shortDescription,
                    affiliation: currentBillTo?.companyName,
                    discount: discountTotal,
                    index: idx,
                    item_brand: cartLine.brand?.name,
                    location_id: locationId,
                    price: cartLine.pricing?.unitNetPrice,
                    quantity: cartLine.qtyOrdered,
                    ...parseProductCategories(cartLine.properties?.categories),
                })),
                brand_code: currentBillTo?.properties?.brand,
                exclusive: currentBillTo?.properties?.exclusive,
                group_descr: currentBillTo?.properties?.groupDescription,
            },
        };

        dataLayer.push(googleAnalyticsData);
    }
};

export const chain = [XngGaCheckoutStepsEvent];

const xngGaCheckoutStepsEvent = createHandlerChainRunner(chain, "XngGaCheckoutStepsEvent");
export default xngGaCheckoutStepsEvent;
